import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Chip,
  Paper,
  Divider,
  Stack,
  TextField,
  Button,
  Avatar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import axios from 'axios';
import { endpoint } from '../../resource/Constant';

const TeacherDashboard = () => {
  const [teacher, setTeacher] = useState(null);
  const [edit, setEdit] = useState(false);
  const [profileForm, setProfileForm] = useState({
    full_name: '',
    email: '',
    school_name: '',
    token_limit: 0,
  });
  const [profileMsg, setProfileMsg] = useState('');
  const [profileErr, setProfileErr] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);

  useEffect(() => {
    const fetchTeacherDetails = async () => {
      try {
        const token = localStorage.getItem('school_token');
        const userId = localStorage.getItem('userId');
        const response = await axios.get(
          `${endpoint}/api/getById?id=${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setTeacher(response.data);
        setProfileForm({
          full_name: response.data.full_name || '',
          email: response.data.email || '',
          school_name: response.data.school_name || '',
          token_limit: response.data.token_limit || 0,
        });
      } catch (err) {
        setProfileErr('Error fetching teacher details');
      }
    };

    fetchTeacherDetails();
  }, []);

  // Helper to format time
  const formatTime = (time) => {
    if (!time) return '';
    const [h, m] = time.split(':');
    let hour = parseInt(h, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12;
    return `${hour}:${m} ${ampm}`;
  };

  const handleProfileChange = (e) => {
    setProfileForm({ ...profileForm, [e.target.name]: e.target.value });
  };

  const handleProfileSave = async () => {
    setProfileLoading(true);
    setProfileMsg('');
    setProfileErr('');
    try {
      const token = localStorage.getItem('school_token');
      const id = localStorage.getItem('userId');
      // Always send token_limit (teachers can't edit it, but backend expects it)
      const payload = {
        full_name: profileForm.full_name,
        email: profileForm.email,
        token_limit: profileForm.token_limit,
      };
      // Only send school_name if not a teacher
      
        payload.school_name = profileForm.school_name;
      
      await axios.put(
        `${endpoint}/api/update/user/${id}`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setEdit(false);
      setProfileMsg('Profile updated successfully!');
      setTeacher((prev) => ({
        ...prev,
        ...profileForm,
      }));
    } catch (err) {
      setProfileErr('Failed to update profile.');
    }
    setProfileLoading(false);
  };

  
    let timetableRows = [];
  if (teacher && teacher.subjects && teacher.subjects.length > 0) {
    timetableRows = teacher.subjects.map((cls) => ({
      standard: cls.standard,
      subject: cls.subject,
      start: formatTime(cls.start_time),
      end: formatTime(cls.end_time),
    }));
  }

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      <Grid container spacing={3}>
        {/* Profile Section */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3, boxShadow: 4, mb: 2, background: 'linear-gradient(120deg, #e3f2fd 60%, #fce4ec 100%)' }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Avatar sx={{ bgcolor: '#1976d2', width: 56, height: 56, fontWeight: 700, fontSize: 26 }}>
                  {profileForm.full_name?.[0]?.toUpperCase() || '?'}
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2' }}>
                  My Profile
                </Typography>
              </Stack>
              <Stack spacing={2}>
                <TextField
                  label="Full Name"
                  name="full_name"
                  value={profileForm.full_name}
                  onChange={handleProfileChange}
                  InputProps={{ readOnly: !edit }}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Email"
                  name="email"
                  value={profileForm.email}
                  onChange={handleProfileChange}
                  InputProps={{ readOnly: !edit }}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="School Name"
                  name="school_name"
                  value={profileForm.school_name}
                  onChange={handleProfileChange}
                  InputProps={{
                    readOnly: true, // Always readOnly for teachers
                  }}
                  fullWidth
                  size="small"
                  helperText={teacher?.role === 'ROLE_TEACHER' ? "Contact admin to change school name" : ""}
                />
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Tokens Used"
                    value={teacher?.tokens_used || 0}
                    InputProps={{ readOnly: true }}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Token Limit"
                    value={profileForm.token_limit}
                    InputProps={{ readOnly: true }}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                </Stack>
              </Stack>
              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                {!edit ? (
                  <Button variant="outlined" onClick={() => setEdit(true)} fullWidth>
                    Edit
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      onClick={handleProfileSave}
                      disabled={profileLoading}
                      fullWidth
                    >
                      {profileLoading ? 'Saving...' : 'Save'}
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => {
                        setEdit(false);
                        setProfileForm({
                          full_name: teacher?.full_name || '',
                          email: teacher?.email || '',
                          school_name: teacher?.school_name || '',
                          token_limit: teacher?.token_limit || 0,
                        });
                        setProfileErr('');
                        setProfileMsg('');
                      }}
                      fullWidth
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </Box>
              {profileMsg && <Alert severity="success" sx={{ mt: 2 }}>{profileMsg}</Alert>}
              {profileErr && <Alert severity="error" sx={{ mt: 2 }}>{profileErr}</Alert>}
            </CardContent>
          </Card>
        </Grid>

        {/* Timetable Section */}
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 2, borderRadius: 3, boxShadow: 3 }}>
            <CardContent>
              <Typography variant="h5" color="primary" gutterBottom sx={{ fontWeight: 700 }}>
                Daily Time Table
              </Typography>
              {timetableRows.length > 0 ? (
                <TableContainer component={Paper} sx={{ borderRadius: 2, background: '#f5faff' }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ background: '#e3f2fd' }}>
                        <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>Class</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>Subject</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>Start Time</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>End Time</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {timetableRows.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontWeight: 600 }}>{row.standard}</TableCell>
                          <TableCell sx={{ textTransform: 'capitalize' }}>{row.subject}</TableCell>
                          <TableCell>{row.start}</TableCell>
                          <TableCell>{row.end}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="text.secondary" sx={{ textAlign: 'center', my: 2 }}>
                  No classes assigned.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Announcements Section */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom sx={{ fontWeight: 700 }}>
                Announcements
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="body2" sx={{ mb: 1 }}>
                <b>PTM Scheduled:</b> Parent-Teacher Meeting on 25th May at 10 AM.
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="body2">
                <b>Annual Sports:</b> Annual Sports Day on 30th May.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TeacherDashboard;